import { IconButton, TextField } from "@material-ui/core";
import { Search, PhotoCamera, Close, CheckCircle, Favorite, LocalHospital, Restaurant, ArrowBackIosNew, Add, Phone } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import PhotoUploadModal from "../common/PhotoUploadModal";
import "./StartNewOpd.css";

import {
  addDoc,
  collection,
  doc,
  updateDoc,
  arrayUnion,
  query,
  onSnapshot,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig/firebase";

import { useNavigate } from "react-router-dom";
import AddNewPt from "../patients/AddNewPt";

function StartNewOpd() {
  let navigate = useNavigate();
  const [inputText, setInputText] = useState("");
  const [searchData, setSearchData] = useState(inputText === "" ? false : true);
  const [data, setData] = useState([]);
  const [date] = useState(new Date(Date.now()));
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [pendingOpdData, setPendingOpdData] = useState(null);
  const [opdEntryCreated, setOpdEntryCreated] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Today's Vitals & Medical History Modal States
  const [vitalsModalOpen, setVitalsModalOpen] = useState(false);
  const [activePatientData, setActivePatientData] = useState(null);
  const [todaysBP, setTodaysBP] = useState("");
  const [eatenSomething, setEatenSomething] = useState("");
  const [breastfeeding, setBreastfeeding] = useState("");
  const [pregnancy, setPregnancy] = useState("");
  const [extraMedication, setExtraMedication] = useState("");
  const [medicalConditions, setMedicalConditions] = useState({
    diabetes: false,
    bp: false,
    heartProblem: false,
    thyroid: false,
    allergy: false,
    hepatitis: false,
    hiv: false,
    bloodThinner: false,
  });

  const handleConditionChange = (condition) => {
    setMedicalConditions((prev) => ({
      ...prev,
      [condition]: !prev[condition]
    }));
  };

  // Scroll Pagination States (30 items per batch)
  const [fetchLimit, setFetchLimit] = useState(30);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setFetchLimit(30);
    setHasMore(true);
  }, [inputText]);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (hasMore && !loadingMore && !searchData) {
        setFetchLimit((prev) => prev + 30);
      }
    }
  };

  const openVitalsModalForPatient = (ptData) => {
    setActivePatientData(ptData);
    // Today's Vitals are reset FRESH for today (do not fetch old visit vitals)
    setTodaysBP("");
    setEatenSomething("");
    setBreastfeeding("");
    setPregnancy("");
    // Permanent history & medications are pre-filled from master patient record
    setExtraMedication(ptData.extraMedication || "");
    setMedicalConditions(ptData.medicalHistory || {
      diabetes: false,
      bp: false,
      heartProblem: false,
      thyroid: false,
      allergy: false,
      hepatitis: false,
      hiv: false,
      bloodThinner: false,
    });
    setVitalsModalOpen(true);
  };

  const handlePatientClick = (item) => {
    const originalptId = item.originalptId || item.docid || item.id;
    const ptObj = {
      id: item.opdId || item.id,
      name: item.name || item.ptName,
      phNumber: item.phoneNumber || item.phNumber,
      gender: item.gender,
      age: item.age,
      TotalAmount: item.TotalAmount || 0,
      totalPaid: item.paid || item.totalPaid || 0,
      ptCreatedDate: item.ptCreatedDate || item.created,
      originalptId: originalptId,
      xrayFolderNumber: item.xrayFolderNumber || "",
      photoURL: item.photoURL || "",
      todaysBP: item.todaysBP || "",
      eatenSomething: item.eatenSomething || "Yes",
      breastfeeding: item.breastfeeding || "No",
      pregnancy: item.pregnancy || "No",
      extraMedication: item.extraMedication || "",
      medicalHistory: item.medicalHistory || {
        diabetes: false, bp: false, heartProblem: false, thyroid: false, allergy: false, hepatitis: false, hiv: false, bloodThinner: false
      }
    };

    // Check if photoURL is undefined or empty
    if (!ptObj.photoURL || ptObj.photoURL.trim() === "") {
      setSelectedPatient({ id: originalptId, name: ptObj.name });
      setPendingOpdData(ptObj);
      setOpdEntryCreated(false);
      setPhotoModalOpen(true);
      return;
    }
    
    // If photo exists, open Vitals modal first
    openVitalsModalForPatient(ptObj);
  };

  const handlePhotoUploaded = (newPhotoURL) => {
    if (pendingOpdData && !opdEntryCreated) {
      const updatedPt = { ...pendingOpdData, photoURL: newPhotoURL };
      setPhotoModalOpen(false);
      setSelectedPatient(null);
      setPendingOpdData(null);
      setOpdEntryCreated(false);
      openVitalsModalForPatient(updatedPt);
    } else {
      setPhotoModalOpen(false);
      setSelectedPatient(null);
      setPendingOpdData(null);
      setOpdEntryCreated(false);
    }
  };

  const handlePhotoModalClose = () => {
    setPhotoModalOpen(false);
    setSelectedPatient(null);
    setPendingOpdData(null);
    setOpdEntryCreated(false);
  };

  const handleConfirmVitalsAndCreateOpd = async () => {
    if (!activePatientData) return;
    if (!eatenSomething || eatenSomething.trim() === "") {
      alert("Please select whether the patient has eaten something today (Yes / No).");
      return;
    }

    if (activePatientData.gender === "Female" || activePatientData.gender === "female") {
      if (!breastfeeding || breastfeeding.trim() === "") {
        alert("Please select whether the patient is breastfeeding (Yes / No).");
        return;
      }
      if (!pregnancy || pregnancy.trim() === "") {
        alert("Please select whether the patient is pregnant (Yes / No).");
        return;
      }
    }

    setVitalsModalOpen(false);
    await createOpdEntry(
      activePatientData.id,
      activePatientData.name,
      activePatientData.phNumber,
      activePatientData.gender,
      activePatientData.age,
      activePatientData.TotalAmount,
      activePatientData.totalPaid,
      activePatientData.ptCreatedDate,
      activePatientData.originalptId,
      activePatientData.xrayFolderNumber,
      activePatientData.photoURL,
      {
        todaysBP,
        eatenSomething,
        breastfeeding,
        pregnancy,
        extraMedication,
        medicalConditions
      }
    );
  };

  const createOpdEntry = async (
    id,
    name,
    phNumber,
    gender,
    age,
    TotalAmount,
    totalPaid,
    ptCreatedDate,
    originalptId,
    xrayFolderNumber,
    photoURL,
    vitals = {}
  ) => {
    let datew = JSON.stringify(date);
    datew = datew.slice(1, 11);

    let ptCreatedDateNew = datew;
    if (ptCreatedDate && ptCreatedDate.seconds) {
      const timestamp = Timestamp.fromMillis(ptCreatedDate.seconds * 1000 + ptCreatedDate.nanoseconds / 1000000);
      const dater = timestamp.toDate();
      const year = dater.getFullYear();
      const month = String(dater.getMonth() + 1).padStart(2, '0');
      const day = String(dater.getDate()).padStart(2, '0');
      ptCreatedDateNew = `${year}-${month}-${day}`;
    }

    const opdData = {
      gender: gender,
      phNumber: phNumber,
      age: age,
      ptId: id,
      ptName: name,
      date: datew,
      ptCreatedDate: ptCreatedDateNew,
      created: serverTimestamp(),
      originalptId: originalptId,
      photoURL: photoURL || "",
      xrayFolderNumber: xrayFolderNumber || "",
      todaysBP: vitals.todaysBP || "",
      eatenSomething: vitals.eatenSomething || "Yes",
      breastfeeding: (gender === "Female" || gender === "female") ? (vitals.breastfeeding || "No") : "N/A",
      pregnancy: (gender === "Female" || gender === "female") ? (vitals.pregnancy || "No") : "N/A",
      extraMedication: vitals.extraMedication || "",
      medicalHistory: vitals.medicalConditions || {
        diabetes: false, bp: false, heartProblem: false, thyroid: false, allergy: false, hepatitis: false, hiv: false, bloodThinner: false
      },
      status: "pending",
      opdPay: 0,
      TotalAmount: TotalAmount || 0,
      paid: totalPaid || 0,
      nextApptOption: "none",
      nextApptType: "None",
      nextApptWork: "",
      nextApptDate: "",
      nextApptDuration: "",
    };

    setShowAlert(true);
    await addDoc(collection(db, "opdEntry"), opdData);

    // Permanently update medicalHistory & extraMedication in patientProfile master document and push visit record to visitHistory array
    if (originalptId) {
      try {
        const visitRecord = {
          date: datew,
          todaysBP: vitals.todaysBP || "",
          eatenSomething: vitals.eatenSomething || "Yes",
          breastfeeding: (gender === "Female" || gender === "female") ? (vitals.breastfeeding || "No") : "N/A",
          pregnancy: (gender === "Female" || gender === "female") ? (vitals.pregnancy || "No") : "N/A",
          extraMedication: vitals.extraMedication || "",
          medicalHistory: vitals.medicalConditions || {},
          created: new Date().toISOString()
        };

        const ptRef = doc(db, "patientProfile", originalptId);
        await updateDoc(ptRef, {
          medicalHistory: vitals.medicalConditions || {},
          extraMedication: vitals.extraMedication || "",
          todaysBP: vitals.todaysBP || "",
          eatenSomething: vitals.eatenSomething || "Yes",
          breastfeeding: (gender === "Female" || gender === "female") ? (vitals.breastfeeding || "No") : "N/A",
          pregnancy: (gender === "Female" || gender === "female") ? (vitals.pregnancy || "No") : "N/A",
          visitHistory: arrayUnion(visitRecord)
        });
      } catch (ptErr) {
        console.error("Error updating permanent patientProfile data:", ptErr);
      }
    }

    setShowAlert(false);
    navigate("/opd");
  };

  useEffect(() => {
    const abc = inputText.toLowerCase().trim();
    if (!abc) {
      setData([]);
      setSearchData(false);
      setLoadingMore(false);
      setHasMore(false);
      return;
    }

    if (fetchLimit === 30) {
      setSearchData(true);
    } else {
      setLoadingMore(true);
    }

    // 4-second debounce timeout before executing Firestore query
    const timer = setTimeout(() => {
      const patientProfile = collection(db, "patientProfile");
      const qd = query(
        patientProfile, 
        where("namearray", "array-contains", abc),
        orderBy("created", "desc"),
        limit(fetchLimit)
      );

      const unsub = onSnapshot(qd, (snap) => {
        let newArray = [];

        snap.forEach((doc) => {
          newArray.push({
            ...doc.data(),
            id: doc.id,
            opdId: doc.get("ptId"),
            name: doc.get("name"),
            phoneNumber: doc.get("phNumber"),
            age: doc.get("age"),
            gender: doc.get("gender"),
            ptCreatedDate: doc.get("created"),
            TotalAmount: doc.get("TotalAmount"),
            paid: doc.get("paid"),
            photoURL: doc.get("photoURL"),
            originalptId: doc.id,
            xrayFolderNumber: doc.get("xrayFolderNumber"),
          });
        });

        setData(newArray);
        setSearchData(false);
        setLoadingMore(false);

        if (snap.docs.length < fetchLimit) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      });

      return () => unsub();
    }, 4000); // 4 sec delay to reduce Firestore data fetch

    return () => clearTimeout(timer);
  }, [inputText, fetchLimit]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Sleek Header Bar with Back Navigation */}
      <Box
        sx={{
          height: "52px",
          background: "linear-gradient(135deg, rgb(5, 2, 43) 0%, rgb(18, 25, 75) 100%)",
          color: "white",
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 4px 16px rgba(5, 2, 43, 0.2)",
          borderRadius: "0 0 20px 20px"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIosNew sx={{ fontSize: "1rem !important" }} />}
          sx={{
            color: "white",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.85rem",
            px: 1.5,
            py: 0.5,
            borderRadius: "12px",
            bgcolor: "rgba(255, 255, 255, 0.1)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" }
          }}
        >
          Back
        </Button>

        <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: "1.05rem", letterSpacing: "0.3px", color: "#ffffff" }}>
          Start New OPD
        </Typography>

        <Box sx={{ width: 65 }} />
      </Box>

      <div className="searchBar" style={{ maxWidth: "700px", margin: "0 auto", padding: "16px 16px 8px 16px" }}>
        {showAlert === true ? (
          <Stack
            sx={{ width: "100%", position: "fixed", top: "12px", zIndex: "999", px: 2 }}
            spacing={2}
          >
            <Alert severity="success" sx={{ borderRadius: "12px", fontWeight: 700 }}>
              <AlertTitle sx={{ fontWeight: 800 }}>Success</AlertTitle>
              Patient added to — <strong>OPD Queue</strong>
            </Alert>
          </Stack>
        ) : null}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            label="Search Patient by Name or Phone Number"
            id="outlined-basic"
            value={inputText}
            InputProps={{
              style: { fontSize: "1rem" },
              endAdornment: (
                <InputAdornment position="end">
                  {searchData ? (
                    <CircularProgress size={20} sx={{ color: "rgb(5, 2, 43)" }} />
                  ) : (
                    <IconButton>
                      <Search />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setInputText(e.target.value);
            }}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: "#ffffff",
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                "& fieldset": { borderColor: "#cbd5e1" },
                "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
              }
            }}
          />
          <AddNewPt />
        </Box>
      </div>

      <div style={{ display: "flex", width: "100%" }}>
        <div className="rightBa" style={{ width: "100%" }}>
          <div
            style={{
              width: "100%",
              overflowY: "scroll",
              height: "63vh",
              overflowX: "hidden",
            }}
            onScroll={handleScroll}
          >
            {searchData ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "20px",
                  padding: "10px",
                  flexDirection: "column",
                }}
              >
                <div>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "20px",
                        padding: "10px",
                        flexDirection: "column",
                      }}
                    >
                      Searching...
                    </div>
                  </Box>
                </div>
              </div>
            ) : (
              <div
                className="search-results"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
              
                 
              
               

                {data.map((item, index) => (
                  <div
                    key={item.id}
                    className={`patientBox1 stagger-item smooth-transition ${!item.photoURL ? 'no-photo-glow' : ''}`}
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      height :'auto',
                      marginBottom: "12px",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      animationDelay: `${index * 0.1}s`
                    }}
                    onClick={() => handlePatientClick(item)}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px",
                        // height:'80vh'
                        // height: "auto",
                        // minHeight: "40px"
                      }}
                    >
                      {/* Left Side - Avatar and Patient Info */}
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                        {/* Patient Avatar */}
                        <div style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#e9ecef",
                          border: item.photoURL ? "2px solid #4caf50" : "2px solid rgba(12, 17, 52, 0.6)",
                          flexShrink: 0,
                          position: "relative"
                        }}>
                          {item.photoURL ? (
                            <img
                              src={item.photoURL}
                              alt={item.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={!item.photoURL ? 'photo-camera-bounce' : ''}
                            style={{
                              display: item.photoURL ? 'none' : 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(12, 17, 52, 0.7)',
                              color: 'white'
                            }}
                          >
                            <PhotoCamera style={{ fontSize: "20px" }} />
                          </div>
                          {!item.photoURL && (
                            <div
                              className="no-photo-pulse"
                              style={{
                                position: 'absolute',
                                bottom: '-2px',
                                right: '-2px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#e74c3c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            >
                              !
                            </div>
                          )}
                        </div>

                        {/* Patient Details */}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              color: "black",
                              marginBottom: "4px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px"
                            }}
                          >
                            {item.name}
                            {!item.photoURL && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  backgroundColor: "#e74c3c",
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontWeight: "500"
                                }}
                              >
                                Photo Required
                              </span>
                            )}
                          </div>
                          <div style={{ 
                            fontSize: "12px", 
                            color: "#6c757d",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            flexWrap: "wrap"
                          }}>
                            <span>{item.age} years • {item.gender}</span>
                            {(() => {
                              const ptCreated = item.created || item.ptCreatedDate;
                              let createdDisplay = "";
                              if (ptCreated) {
                                if (typeof ptCreated === "string") {
                                  createdDisplay = ptCreated;
                                } else if (ptCreated.seconds) {
                                  const d = new Date(ptCreated.seconds * 1000);
                                  createdDisplay = d.toISOString().slice(0, 10);
                                }
                              }
                              return createdDisplay ? (
                                <span style={{ color: "#2b2d42", fontWeight: "600", backgroundColor: "#edf2f7", padding: "1px 6px", borderRadius: "4px", fontSize: "11px" }}>
                                  📅 {createdDisplay}
                                </span>
                              ) : null;
                            })()}
                            {!item.photoURL && (
                              <span style={{ color: "#e74c3c", fontWeight: "500" }}>
                                • Must add photo to proceed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right Side - Phone & Start OPD Action Button */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        {item.phNumber && (
                          <a
                            href={`tel:${item.phNumber}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "6px 10px",
                              backgroundColor: "#e8f5e9",
                              borderRadius: "20px",
                              textDecoration: "none",
                              color: "#2e7d32",
                              fontWeight: "600",
                              fontSize: "12px"
                            }}
                          >
                            <Phone style={{ fontSize: "14px" }} />
                            <span>{item.phNumber}</span>
                          </a>
                        )}

                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add style={{ fontSize: "16px" }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePatientClick(item);
                          }}
                          style={{
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "700",
                            textTransform: "none",
                            background: "linear-gradient(135deg, rgb(5, 2, 43) 0%, rgb(20, 30, 80) 100%)",
                            color: "white",
                            padding: "6px 14px",
                            boxShadow: "0 3px 10px rgba(5, 2, 43, 0.25)",
                            whiteSpace: "nowrap"
                          }}
                        >
                          Start OPD
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Photo Upload Modal */}
      <PhotoUploadModal
        open={photoModalOpen}
        onClose={handlePhotoModalClose}
        patientId={selectedPatient?.id}
        patientName={selectedPatient?.name}
        onPhotoUploaded={handlePhotoUploaded}
      />

      {/* Today's OPD Vitals & Details Modal */}
      <Dialog
        open={vitalsModalOpen}
        onClose={() => setVitalsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(5, 2, 43, 0.25)"
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: "linear-gradient(135deg, rgb(5, 2, 43), rgb(20, 30, 80))",
          color: "white",
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <LocalHospital sx={{ fontSize: "1.8rem", color: "#60a5fa" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "1.1rem", lineHeight: 1.2 }}>
                Today's Patient Details & Vitals
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                {activePatientData?.name} ({activePatientData?.age} yrs • {activePatientData?.gender})
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setVitalsModalOpen(false)} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5, bgcolor: "#f8fafc" }}>
          {/* Today's Vitals Card */}
          <Box sx={{
            bgcolor: "#ffffff",
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Today's Vitals
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <TextField
                fullWidth
                label="Today's Blood Pressure (BP)"
                placeholder="e.g. 120/80"
                value={todaysBP}
                onChange={(e) => setTodaysBP(e.target.value)}
                size="small"
                variant="outlined"
                helperText="Systolic / Diastolic (e.g. 120/80)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Favorite sx={{ color: "#d32f2f", fontSize: "1.1rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                        mmHg
                      </Typography>
                    </InputAdornment>
                  )
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: "#f8fafc",
                    "& fieldset": { borderColor: "#cbd5e1" },
                    "&:hover fieldset": { borderColor: "rgb(5, 2, 43)" },
                    "&.Mui-focused fieldset": { borderColor: "rgb(5, 2, 43)", borderWidth: 2 }
                  }
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                  Eaten Something Today? <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <RadioGroup row value={eatenSomething} onChange={(e) => setEatenSomething(e.target.value)}>
                  <FormControlLabel value="Yes" control={<Radio size="small" sx={{ color: "#2e7d32", "&.Mui-checked": { color: "#2e7d32" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Yes</Typography>} />
                  <FormControlLabel value="No" control={<Radio size="small" sx={{ color: "#d32f2f", "&.Mui-checked": { color: "#d32f2f" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>No</Typography>} />
                </RadioGroup>
              </Box>

              {(activePatientData?.gender === "Female" || activePatientData?.gender === "female") && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                      Breastfeeding? <span style={{ color: "#d32f2f" }}>*</span>
                    </Typography>
                    <RadioGroup row value={breastfeeding} onChange={(e) => setBreastfeeding(e.target.value)}>
                      <FormControlLabel value="Yes" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Yes</Typography>} />
                      <FormControlLabel value="No" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>No</Typography>} />
                    </RadioGroup>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>
                      Pregnancy? <span style={{ color: "#d32f2f" }}>*</span>
                    </Typography>
                    <RadioGroup row value={pregnancy} onChange={(e) => setPregnancy(e.target.value)}>
                      <FormControlLabel value="Yes" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Yes</Typography>} />
                      <FormControlLabel value="No" control={<Radio size="small" sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />} label={<Typography variant="body2" sx={{ fontWeight: 700 }}>No</Typography>} />
                    </RadioGroup>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Medical History Checklist */}
          <Box sx={{
            bgcolor: "#ffffff",
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            gap: 1.5
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Medical History Checklist
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.diabetes} onChange={() => handleConditionChange("diabetes")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Diabetes</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.bp} onChange={() => handleConditionChange("bp")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>BP / Hypertension</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.heartProblem} onChange={() => handleConditionChange("heartProblem")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Heart Problem</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.thyroid} onChange={() => handleConditionChange("thyroid")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Thyroid</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.allergy} onChange={() => handleConditionChange("allergy")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Allergy</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.hepatitis} onChange={() => handleConditionChange("hepatitis")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Hepatitis</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.hiv} onChange={() => handleConditionChange("hiv")} sx={{ color: "rgb(5, 2, 43)", "&.Mui-checked": { color: "rgb(5, 2, 43)" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>HIV</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={medicalConditions.bloodThinner} onChange={() => handleConditionChange("bloodThinner")} sx={{ color: "#d32f2f", "&.Mui-checked": { color: "#d32f2f" } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 700, color: "#b71c1c" }}>Blood Thinner</Typography>}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: "#ffffff", borderTop: "1px solid #f1f5f9", justifyContent: "space-between" }}>
          <Button onClick={() => setVitalsModalOpen(false)} sx={{ color: "#64748b", fontWeight: 700, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmVitalsAndCreateOpd}
            startIcon={<CheckCircle />}
            sx={{
              background: "linear-gradient(135deg, rgb(5, 2, 43), rgb(20, 30, 80))",
              borderRadius: "24px",
              px: 3.5,
              py: 1.1,
              fontWeight: 800,
              fontSize: "0.9rem",
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(5, 2, 43, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, rgb(10, 5, 60), rgb(30, 42, 100))",
                transform: "translateY(-1px)"
              }
            }}
          >
            Confirm & Start OPD
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StartNewOpd;
